import { useState } from "react";
import { X, Swords, Coins, Fuel, Lock, ChevronRight, Radar, ShieldCheck, Skull, Truck, Crosshair, ArrowUpCircle, Building2 } from "lucide-react";
import { SpaceHomeBtn } from "./SpaceNav";
import SurfaceSweep from "./SurfaceSweep";
import { loadSurface, clearSurfaceSector, sectorUnlocked, surfaceSectorReward,
  vehicleArmorForLevel, nextTurret, TURRET_LABEL, armorUpgradeCost, turretUpgradeCost,
  upgradeVehicleArmor, upgradeVehicleTurret, VEHICLE_ARMOR_CAP,
  BUILDINGS, canBuild, constructBuilding, clearedSectorCount, builtCount, vehicleBayBonusArmor, addSurfaceResources } from "./stateManager";

const S = "/game/surface";
// Lv20 PvP zone — hostile NPC bunkers revealed once a Radar Tower is built.
const RAID_TARGETS = [
  { id: "raider_camp", name: "Raider Camp", difficulty: 15 },
  { id: "outrider_nest", name: "Outrider Nest", difficulty: 19 },
  { id: "warband_hold", name: "Warband Stronghold", difficulty: 24 },
];

export default function SurfaceCompound({ onBack }) {
  const [surface, setSurface] = useState(() => loadSurface());
  const [selected, setSelected] = useState(null); // sector selected for briefing
  const [view, setView] = useState("map");         // map | combat
  const [banner, setBanner] = useState(null);      // { name, reward }
  const [raidTarget, setRaidTarget] = useState(null);

  const sectors = Object.values(surface.sectors);
  const res = surface.resources;
  const v = surface.vehicle || {};
  const radar = !!surface.buildings?.radar_tower;
  const shield = !!surface.buildings?.shield_generator;

  const onSelect = (sec) => {
    if (sec.type === "safe" || sec.cleared) return;
    if (!sectorUnlocked(surface, sec)) return;
    setSelected(sec);
  };

  const launch = () => { if (selected) setView("combat"); };

  const onSweepWin = () => {
    const { surface: next, reward } = clearSurfaceSector(selected.id);
    setSurface(next);
    setBanner({ name: selected.name, reward });
    setSelected(null);
    setView("map");
    setTimeout(() => setBanner(null), 4200);
  };

  const doArmor = () => { const r = upgradeVehicleArmor(); if (r.ok) setSurface(r.surface); };
  const doTurret = () => { const r = upgradeVehicleTurret(); if (r.ok) setSurface(r.surface); };
  const doBuild = (id) => { const r = constructBuilding(id); if (r.ok) setSurface(r.surface); };

  const startRaid = (t) => { setRaidTarget(t); setSelected(null); setView("combat"); };
  const onRaidWin = () => {
    const reward = surfaceSectorReward(raidTarget.difficulty);
    setSurface(addSurfaceResources(reward.titanium_alloy, reward.bio_fuel));
    setBanner({ name: raidTarget.name, reward });
    setRaidTarget(null); setView("map");
    setTimeout(() => setBanner(null), 4200);
  };

  if (view === "combat" && (selected || raidTarget)) {
    const tgt = raidTarget || selected;
    const vv = surface.vehicle || {};
    return (
      <SurfaceSweep
        sector={tgt}
        reward={surfaceSectorReward(tgt.difficulty)}
        vehicle={{ armor: vehicleArmorForLevel(vv.armor_level) + vehicleBayBonusArmor(surface), turret: vv.turret_tier }}
        onWin={raidTarget ? onRaidWin : onSweepWin}
        onBack={() => { setView("map"); setRaidTarget(null); }}
      />
    );
  }

  return (
    <div className="ss-viewport bhub scomp" data-testid="surface-compound">
      <div className="ss-atmosphere" />
      <SpaceHomeBtn onClick={onBack} testid="surface-compound-back" />

      <div className="bhub-head" data-testid="surface-compound-head">
        <span className="bhub-title">SURFACE EXPANSION COMPOUND</span>
        <span className="bhub-sub">Phase B · Desert Sector Grid · CC12 Breach</span>
      </div>

      {/* Surface economy bar */}
      <div data-testid="surface-economy" style={{ display: "flex", gap: 10, justifyContent: "center", padding: "0 12px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 10, background: "rgba(20,14,8,0.9)", border: "1px solid #8b5a2b" }}>
          <Coins size={15} color="#e5e4e2" />
          <span style={{ fontSize: 12, color: "#cfc2a8" }}>Titanium Alloy</span>
          <b data-testid="surface-titanium" style={{ color: "#fff" }}>{res.titanium_alloy}</b>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 10, background: "rgba(20,14,8,0.9)", border: "1px solid #4caf50" }}>
          <Fuel size={15} color="#6ee06e" />
          <span style={{ fontSize: 12, color: "#cfc2a8" }}>Bio-Fuel</span>
          <b data-testid="surface-biofuel" style={{ color: "#8affc9" }}>{res.bio_fuel}</b>
        </div>
      </div>

      {/* Compound art banner */}
      <div style={{ padding: "0 12px 10px" }}>
        <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(230,140,70,0.4)" }}>
          <img src={`${S}/compound_map.jpg`} alt="Surface compound" data-testid="surface-compound-map" style={{ width: "100%", height: 120, objectFit: "cover", display: "block", opacity: 0.85 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,rgba(10,7,4,0.1),rgba(10,7,4,0.85))" }} />
          <div style={{ position: "absolute", left: 12, right: 12, bottom: 8, color: "#ffd8b0", fontSize: 10, letterSpacing: 0.3, lineHeight: 1.25 }}>
            🏜️ Toxic sandstorms · Sprawlers &amp; Hatchlings roam the fog
          </div>
        </div>
      </div>

      {/* Vehicle Bay — spend surface resources to harden the combat vehicle */}
      <div data-testid="surface-vehicle-bay" style={{ padding: "0 12px 10px" }}>
        <div style={{ background: "rgba(14,11,8,0.9)", border: "1px solid rgba(230,140,70,0.4)", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#ffd8b0", fontWeight: 800, fontSize: 13, marginBottom: 8 }}>
            <Truck size={16} /> Vehicle Bay · Armored Pickup
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: "#cfc2a8", marginBottom: 10 }}>
            <span data-testid="surface-vehicle-armor" style={{ display: "flex", alignItems: "center", gap: 5 }}><ShieldCheck size={13} /> Armor Lv {v.armor_level} <b style={{ color: "#fff" }}>({vehicleArmorForLevel(v.armor_level)} plate)</b></span>
            <span data-testid="surface-vehicle-turret" style={{ display: "flex", alignItems: "center", gap: 5 }}><Crosshair size={13} /> {TURRET_LABEL[v.turret_tier]}</span>
          </div>
          {(() => {
            const maxed = v.armor_level >= VEHICLE_ARMOR_CAP;
            const cost = armorUpgradeCost(v.armor_level);
            const afford = !maxed && res.titanium_alloy >= cost.titanium_alloy && res.bio_fuel >= cost.bio_fuel;
            return (
              <button data-testid="surface-upgrade-armor" onClick={doArmor} disabled={!afford}
                style={{ width: "100%", marginBottom: 8, padding: "9px", borderRadius: 10, cursor: afford ? "pointer" : "not-allowed",
                  background: afford ? "rgba(55,224,160,0.16)" : "rgba(255,255,255,0.05)", border: `1px solid ${afford ? "rgba(55,224,160,0.6)" : "rgba(255,255,255,0.12)"}`,
                  color: afford ? "#8affc9" : "#5b6b82", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <ArrowUpCircle size={14} /> {maxed ? "Armor Maxed" : `Reinforce Armor → Lv ${v.armor_level + 1} · ${cost.titanium_alloy}T / ${cost.bio_fuel}F`}
              </button>
            );
          })()}
          {(() => {
            const nt = nextTurret(v.turret_tier);
            if (!nt) return <div style={{ fontSize: 11, color: "#8affc9", textAlign: "center" }}>✓ Missile Pod installed — turret maxed</div>;
            const cost = turretUpgradeCost(nt);
            const afford = res.titanium_alloy >= cost.titanium_alloy && res.bio_fuel >= cost.bio_fuel;
            return (
              <button data-testid="surface-upgrade-turret" onClick={doTurret} disabled={!afford}
                style={{ width: "100%", padding: "9px", borderRadius: 10, cursor: afford ? "pointer" : "not-allowed",
                  background: afford ? "rgba(255,152,0,0.16)" : "rgba(255,255,255,0.05)", border: `1px solid ${afford ? "rgba(255,152,0,0.6)" : "rgba(255,255,255,0.12)"}`,
                  color: afford ? "#ffd8a8" : "#5b6b82", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Crosshair size={14} /> Install {TURRET_LABEL[nt]} · {cost.titanium_alloy}T / {cost.bio_fuel}F
              </button>
            );
          })()}
        </div>
      </div>

      {/* Construction — build structures on cleared sectors */}
      <div data-testid="surface-construction" style={{ padding: "0 12px 10px" }}>
        <div style={{ background: "rgba(14,11,8,0.9)", border: "1px solid rgba(79,195,255,0.3)", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#bfe6ff", fontWeight: 800, fontSize: 13 }}><Building2 size={16} /> Construction</span>
            <span data-testid="surface-plots" style={{ fontSize: 11, color: "#9a8f7a" }}>Plots: {clearedSectorCount(surface) - builtCount(surface)} free</span>
          </div>
          {Object.values(BUILDINGS).map((bld) => {
            const built = !!surface.buildings?.[bld.id];
            const chk = canBuild(surface, bld.id);
            const Icon = bld.id === "radar_tower" ? Radar : bld.id === "shield_generator" ? ShieldCheck : Truck;
            return (
              <div key={bld.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ maxWidth: "56%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: built ? "#8affc9" : "#fff", fontWeight: 700, fontSize: 12 }}><Icon size={13} /> {bld.name}</div>
                  <div style={{ fontSize: 10, color: "#9a8f7a", marginTop: 2 }}>{bld.effect}</div>
                </div>
                {built ? (
                  <span data-testid={`surface-built-${bld.id}`} style={{ fontSize: 11, color: "#8affc9", fontWeight: 800 }}>✓ BUILT</span>
                ) : (
                  <button data-testid={`surface-build-${bld.id}`} onClick={() => doBuild(bld.id)} disabled={!chk.ok}
                    style={{ padding: "7px 10px", borderRadius: 9, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", cursor: chk.ok ? "pointer" : "not-allowed",
                      background: chk.ok ? "rgba(79,195,255,0.16)" : "rgba(255,255,255,0.05)", border: `1px solid ${chk.ok ? "rgba(79,195,255,0.6)" : "rgba(255,255,255,0.12)"}`, color: chk.ok ? "#bfe6ff" : "#5b6b82" }}>
                    {chk.reason === "no_plot" ? "Clear a sector" : `${bld.cost.titanium_alloy}T / ${bld.cost.bio_fuel}F`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Radar-gated Lv20 PvP raid grid + Shield status (building payoffs) */}
      {radar ? (
        <div data-testid="surface-raid-grid" style={{ padding: "0 12px 10px" }}>
          <div style={{ background: "rgba(14,11,8,0.9)", border: "1px solid rgba(255,80,80,0.35)", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#ff9a9a", fontWeight: 800, fontSize: 13, marginBottom: 4 }}>
              <Radar size={16} /> Radar · Hostile Bunkers
              <span data-testid="surface-shield-status" style={{ fontSize: 10, color: shield ? "#8affc9" : "#ff9a6a", marginLeft: "auto" }}>{shield ? "🛡 SHIELD ONLINE" : "⚠ BASE EXPOSED"}</span>
            </div>
            {RAID_TARGETS.map((t) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div><div style={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>{t.name}</div><div style={{ fontSize: 10, color: "#9a8f7a" }}>Threat Lv.{t.difficulty} · Lv20 PvP zone</div></div>
                <button data-testid={`surface-raid-${t.id}`} onClick={() => startRaid(t)} style={{ padding: "7px 12px", borderRadius: 9, fontSize: 11, fontWeight: 700, cursor: "pointer", background: "rgba(255,80,80,0.16)", border: "1px solid rgba(255,80,80,0.6)", color: "#ffb0b0" }}>Raid</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="surface-radar-hint" style={{ padding: "0 12px 10px" }}>
          <div style={{ background: "rgba(14,11,8,0.7)", border: "1px dashed rgba(255,80,80,0.3)", borderRadius: 12, padding: "10px 14px", fontSize: 11, color: "#9a8f7a", textAlign: "center" }}>
            <Radar size={13} /> Build a <b style={{ color: "#ff9a9a" }}>Radar Tower</b> to reveal hostile bunkers &amp; raid the Lv20 PvP zone{shield ? " · 🛡 Base Shield online" : ""}
          </div>
        </div>
      )}

      {/* Sector clearance grid */}
      <div data-testid="surface-sector-grid" style={{ padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
        {sectors.map((sec) => {
          const unlocked = sectorUnlocked(surface, sec);
          const isSafe = sec.type === "safe";
          const locked = !isSafe && !sec.cleared && !unlocked;
          const border = sec.cleared ? "#37e0a0" : locked ? "rgba(255,255,255,0.1)" : selected?.id === sec.id ? "#ff9800" : "#5d4037";
          return (
            <button key={sec.id} data-testid={`surface-sector-${sec.id}`} onClick={() => onSelect(sec)}
              disabled={isSafe || sec.cleared || locked}
              style={{
                textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "12px 14px", borderRadius: 12, cursor: (isSafe || sec.cleared || locked) ? "default" : "pointer",
                background: sec.cleared ? "rgba(30,39,26,0.85)" : "rgba(14,11,8,0.9)",
                border: `1px solid ${border}`, opacity: locked ? 0.55 : 1,
              }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, color: sec.cleared ? "#8affc9" : "#fff", fontWeight: 800, fontSize: 13 }}>
                  {sec.cleared ? <ShieldCheck size={14} /> : locked ? <Lock size={14} /> : <Skull size={14} color="#ff9a6a" />}
                  {sec.name}
                </div>
                <div style={{ fontSize: 11, color: "#9a8f7a", marginTop: 2 }} data-testid={`surface-sector-status-${sec.id}`}>
                  {isSafe ? "SAFE ZONE" : sec.cleared ? "✅ SECURED · fog cleared" : locked ? "🔒 SEALED · clear the prior sector" : "🌫️ FOG OF WAR"}
                </div>
              </div>
              {!isSafe && !sec.cleared && (
                <div style={{ textAlign: "right", color: "#ff9800", fontWeight: 800, fontSize: 12 }}>
                  Threat Lv.{sec.difficulty}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {banner && (
        <div data-testid="surface-clear-banner" style={{
          position: "absolute", left: "50%", bottom: 90, transform: "translateX(-50%)", zIndex: 40,
          background: "rgba(6,10,20,0.92)", border: "1px solid #ff9800", borderRadius: 12, padding: "10px 16px",
          textAlign: "center", boxShadow: "0 0 26px rgba(255,152,0,0.4)", maxWidth: "88%",
        }}>
          <div style={{ color: "#ffd18a", fontWeight: 900, fontSize: 13 }}>SECTOR SECURED · {banner.name}</div>
          <div style={{ color: "#cfe0f5", fontSize: 11, marginTop: 3 }}>+{banner.reward?.titanium_alloy} Titanium · +{banner.reward?.bio_fuel} Bio-Fuel</div>
        </div>
      )}

      {/* Deployment briefing console */}
      {selected && (
        <div className="ss-overlay bhub-room" data-testid="surface-brief" onClick={() => setSelected(null)}>
          <div className="bhub-room-card" onClick={(e) => e.stopPropagation()} style={{ borderColor: "#ff9800" }}>
            <button className="bhub-room-x" data-testid="surface-brief-close" onClick={() => setSelected(null)}><X size={16} /></button>
            <div className="bhub-room-icon" style={{ color: "#ff9a6a", borderColor: "#ff9a6a" }}><Skull size={34} /></div>
            <span className="bhub-room-name" style={{ color: "#ff9a6a" }}>{selected.name}</span>
            <p className="bhub-room-desc">Deploy the squad onto the surface highway and sweep this coordinate clear. Threat Level <b>{selected.difficulty}</b>. Reward: <b style={{ color: "#e5e4e2" }}>+{surfaceSectorReward(selected.difficulty).titanium_alloy} Titanium</b> · <b style={{ color: "#8affc9" }}>+{surfaceSectorReward(selected.difficulty).bio_fuel} Bio-Fuel</b>.</p>
            <button className="ss-enter-btn" data-testid="surface-launch-btn" onClick={launch}
              style={{ borderColor: "rgba(255,152,0,0.7)", background: "rgba(255,152,0,0.16)", color: "#ffd8a8" }}>
              <Swords size={16} /> Launch Topside Tactical Team <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
